import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../lib/site";
import { scrollToHash, scrollTop } from "../lib/lenis";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || location.pathname !== "/" || open;

  const go = (link) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (link.hash) {
      if (location.pathname !== "/") {
        navigate("/" + link.hash);
      } else {
        scrollToHash(link.hash);
      }
    } else {
      navigate(link.to);
      if (link.to === "/") scrollTop();
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        solid ? "bg-charcoal/85 backdrop-blur-xl border-b border-ivory/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-8">
        <a href="/" onClick={go({ to: "/", hash: "" })} aria-label="PNG Landed Excellence home">
          <Logo />
        </a>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.to + link.hash}
              onClick={go(link)}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group relative text-[0.8rem] font-medium uppercase tracking-[0.18em] text-ivory/85 transition-colors duration-300 hover:text-ivory"
            >
              {link.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-ivory/70 transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
          <a
            href="/#contact"
            onClick={go({ to: "/", hash: "#contact" })}
            data-testid="header-cta-speak-to-andy"
            className="group flex items-center gap-2 bg-brand px-5 py-2.5 text-[0.8rem] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:bg-brand-dark"
          >
            Speak to Andy
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </nav>

        <button
          type="button"
          data-testid="mobile-menu-button"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
          className="text-ivory lg:hidden"
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="border-t border-ivory/10 bg-charcoal/95 backdrop-blur-xl lg:hidden"
            aria-label="Mobile"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 py-6">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.to + link.hash}
                  onClick={go(link)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="border-b border-ivory/10 py-4 font-serif text-2xl text-ivory"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="/#contact"
                onClick={go({ to: "/", hash: "#contact" })}
                data-testid="mobile-cta-speak-to-andy"
                className="mt-6 flex items-center justify-center gap-2 bg-brand px-5 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-ivory"
              >
                Speak to Andy
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
